import { useDispatch, useSelector } from "react-redux";
import { MainContainer } from "../../StyledComponents/Dashboard";
import CustomeHeader from "../Admin/Common/CustomeHeader";
import QueryDetailHeader from "./ManualQuoteHeader";
import "./ManualQuote.css";
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import {
  addManualQuote,
  clearManualQuote,
  editManualQuote,
  getAllOriginDestinationCharges,
} from "../../store/ManualQuote/ManualQuoteSlice";
import { getAirlinesListByCode } from "../../store/Markup/markupSlice";
import { getallairports } from "../../store/SearchRates/SearchRatesSlice";
import dayjs from "dayjs";
import { updatenotificationdata } from "./../../store/Admin/Common/CommonSlice";
import { DatePicker } from "antd";
import { useNavigate } from "react-router-dom";
import TextError from "../../forms/TextError";
import SelectMultipleWithSearch from "../Admin/Common/SelectMultipleWithSearch";
import AirlineTabs from "./AirlineTabs";
import FlightOptionCard from "./FlightOptionCard";
import { money, airlineGrandTotal } from "./chargeUtils";

const LABEL_CLS =
  "block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1";

const ADD_SECTION_BTN_STYLE = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "12px 0",
  borderRadius: 12,
  border: "2px dashed #FCB614",
  background: "transparent",
  color: "#FCB614",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const HIGHLIGHT_CARD_STYLE = {
  background: "linear-gradient(135deg, #fffbf0 0%, #fff8e1 100%)",
  border: "1px solid #f0dea3",
  borderRadius: 12,
  padding: "10px 18px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  boxShadow: "0 1px 2px rgba(16,24,40,0.03)",
};

// ── Formik default shapes ───────────────────────────────────────────────────
const makeChargeItem = () => ({ charge: "", unit: "perkg", unitRate: "" });

const makeItinerarySegment = (carrierCode = "") => ({
  carrier: carrierCode,
  operationType: "",
  origin: "",
  destination: "",
  departure: null,
  arrival: null,
  aircraft: "",
  flight: "",
});

const makeFlightOption = (carrierCode = "") => ({
  departureDateAndTime: null,
  arrivalDateAndTime: null,
  transitDuration: "",
  serviceType: "",
  itineraryField: "",
  charges: {
    origin: { items: [makeChargeItem()] },
    destination: { items: [makeChargeItem()] },
    freightAndSurcharges: { items: [makeChargeItem()] },
  },
  itinerary: [makeItinerarySegment(carrierCode)],
});

const makeAirline = () => ({
  carrierCode: "",
  carrierName: "",
  flightOptions: [makeFlightOption()],
});

// ── Validation ───────────────────────────────────────────────────────────────
const chargeItemSchema = Yup.object({
  charge: Yup.string().required("Charge is required"),
  unit: Yup.mixed()
    .oneOf(["perkg", "lumpsum"], "Invalid unit")
    .required("Unit is required"),
  unitRate: Yup.number()
    .typeError("Enter a valid number")
    .required("Rate is required"),
});

const flightOptionSchema = Yup.object({
  departureDateAndTime: Yup.date()
    .nullable()
    .required("Departure date and time is required"),
  arrivalDateAndTime: Yup.date()
    .nullable()
    .required("Arrival date and time is required")
    .test(
      "arrival-after-departure",
      "Arrival must be after departure",
      function (value) {
        const { departureDateAndTime } = this.parent;
        if (!value || !departureDateAndTime) return true;
        return dayjs(value).isAfter(dayjs(departureDateAndTime));
      }
    ),
  transitDuration: Yup.string().required("Transit duration is required"),
  serviceType: Yup.string().required("Service type is required"),
  charges: Yup.object({
    origin: Yup.object({ items: Yup.array().of(chargeItemSchema) }),
    destination: Yup.object({ items: Yup.array().of(chargeItemSchema) }),
    freightAndSurcharges: Yup.object({
      items: Yup.array()
        .of(chargeItemSchema)
        .min(1, "At least one freight charge is required"),
    }),
  }),
  itinerary: Yup.array().of(
    Yup.object().shape({
      carrier: Yup.string().required("Carrier is required"),
      origin: Yup.string().required("Origin is required"),
      destination: Yup.string().required("Destination is required"),
      departure: Yup.string().required("Departure time is required"),
      arrival: Yup.date().nullable().required("Arrival time is required"),
      aircraft: Yup.string().required("Aircraft is required"),
      flight: Yup.string().required("Flight number is required"),
    })
  ),
  itineraryField: Yup.string().when("itinerary", {
    is: (itinerary) =>
      itinerary.some(
        (item) =>
          !item.carrier ||
          !item.origin ||
          !item.destination ||
          !item.departure ||
          !item.arrival ||
          !item.aircraft ||
          !item.flight
      ),
    then: Yup.string().required("Itinerary is required"),
    otherwise: Yup.string().nullable(),
  }),
});

const airlineSchema = Yup.object({
  carrierCode: Yup.string().required("Carrier is required"),
  flightOptions: Yup.array()
    .of(flightOptionSchema)
    .min(1, "At least one flight option is required"),
});

const validationSchema = Yup.object({
  quoteExpiry: Yup.date().nullable().required("Expiry date is required"),
  airlines: Yup.array().of(airlineSchema).min(1, "At least one airline is required"),
});

// ── Cross-row checks that Yup can't express cleanly on its own ─────────────
const validateDuplicateAirlines = (airlines) => {
  for (let i = 0; i < airlines.length; i++) {
    for (let j = i + 1; j < airlines.length; j++) {
      if (
        airlines[i].carrierCode &&
        airlines[i].carrierCode === airlines[j].carrierCode
      ) {
        return "Each airline in this quote must be different";
      }
    }
  }
  return "";
};

const validateDuplicateOptions = (airlines) => {
  for (const airline of airlines) {
    const opts = airline.flightOptions;
    for (let i = 0; i < opts.length; i++) {
      for (let j = i + 1; j < opts.length; j++) {
        const dep1 = dayjs(opts[i].departureDateAndTime);
        const arr1 = dayjs(opts[i].arrivalDateAndTime);
        const dep2 = dayjs(opts[j].departureDateAndTime);
        const arr2 = dayjs(opts[j].arrivalDateAndTime);
        if (dep1.isSame(dep2) && arr1.isSame(arr2)) {
          return "Two flight options for the same airline cannot share the same schedule";
        }
      }
    }
  }
  return "";
};

const validateDuplicateCharges = (airlines) => {
  const hasDuplicateCharge = (items) => {
    const seen = new Set();
    for (const item of items || []) {
      if (!item.charge) continue;
      if (seen.has(item.charge)) return true;
      seen.add(item.charge);
    }
    return false;
  };
  for (const airline of airlines) {
    for (const option of airline.flightOptions) {
      if (
        hasDuplicateCharge(option.charges.origin.items) ||
        hasDuplicateCharge(option.charges.destination.items) ||
        hasDuplicateCharge(option.charges.freightAndSurcharges.items)
      ) {
        return "The same charge cannot be added twice within one section";
      }
    }
  }
  return "";
};

// ── Submit payload (matches the airlines[].flightOptions[] API contract) ──
const computeChargeItem = (item, totalWeight) => {
  const rate = Number(item.unitRate) || 0;
  const isPerKg = item.unit === "perkg";
  const out = { charge: item.charge, unit: item.unit, unitRate: rate };
  if (isPerKg) {
    const weight = Number(totalWeight) || 0;
    out.chargeableWeight = weight;
    out.totalAmount = rate * weight;
  } else {
    out.totalAmount = rate;
  }
  return out;
};

const computeChargeSection = (items, totalWeight) => {
  const computedItems = (items || []).map((item) =>
    computeChargeItem(item, totalWeight)
  );
  return {
    totalAmount: computedItems.reduce((sum, item) => sum + item.totalAmount, 0),
    items: computedItems,
  };
};

const buildSubmitPayload = (values, queryId, totalWeight) => ({
  queryId,
  quoteExpiry: values.quoteExpiry,
  airlines: values.airlines.map((airline) => ({
    carrierCode: airline.carrierCode,
    carrierName: airline.carrierName,
    flightOptions: airline.flightOptions.map((option) => {
      const origin = computeChargeSection(option.charges.origin.items, totalWeight);
      const destination = computeChargeSection(
        option.charges.destination.items,
        totalWeight
      );
      const freightAndSurcharges = computeChargeSection(
        option.charges.freightAndSurcharges.items,
        totalWeight
      );
      return {
        ...(option.optionId ? { optionId: option.optionId } : {}),
        departureDateAndTime: option.departureDateAndTime,
        arrivalDateAndTime: option.arrivalDateAndTime,
        transitDuration: Number(option.transitDuration) || 0,
        serviceType: option.serviceType,
        charges: { origin, destination, freightAndSurcharges },
        itinerary: option.itinerary,
        grandTotal:
          origin.totalAmount + destination.totalAmount + freightAndSurcharges.totalAmount,
      };
    }),
  })),
});

const ManualQuote = () => {
  const dispatch = useDispatch();
  const { queryDetails, manualQuoteDetails } = useSelector(
    (state) => state.manualQuoteSlice
  );
  const [loading, setloading] = useState(false);
  const [chargeList, setChargeList] = useState([]);
  const [airLineList, setairLineList] = useState([]);
  const [airPort, setAirPort] = useState([]);
  const [activeAirlineIndex, setActiveAirlineIndex] = useState(0);
  const navigate = useNavigate();

  const totalWeight = queryDetails?.packagingDetails?.packagingType?.totalweight;
  const originAirport = queryDetails?.originPort?.originAirport || "";
  const destAirport = queryDetails?.destinationPort?.destinationAirport || "";

  const initialValues = {
    quoteExpiry: null,
    airlines: [makeAirline()],
    ...manualQuoteDetails,
  };

  useEffect(() => {
    dispatch(getAllOriginDestinationCharges()).then((res) => {
      setChargeList(
        res?.payload?.map((item) => ({
          label: item?.charge_name,
          value: item?._id,
        }))
      );
    });
    dispatch(getAirlinesListByCode()).then((res) => {
      setairLineList(
        res?.payload?.map((item) => ({
          label: `${item?.carr_code} - ${item?.carr_name}`,
          value: item?.carr_code,
        }))
      );
    });
    dispatch(getallairports()).then((res) => {
      setAirPort(
        res?.payload?.data?.data?.map((item) => ({
          value: `${item.cityName} - (${item.code})`,
          label: `${item.cityName} - (${item.code})`,
        }))
      );
    });
  }, []);

  const onSubmit = (values, setErrors, resetForm) => {
    const formError =
      validateDuplicateAirlines(values.airlines) ||
      validateDuplicateOptions(values.airlines) ||
      validateDuplicateCharges(values.airlines);
    if (formError) {
      setErrors({ formError });
      return;
    }

    const payload = buildSubmitPayload(values, queryDetails?._id, totalWeight);
    setloading(true);

    const handleResult = (result) => {
      setloading(false);
      const data = {
        message: result?.payload?.message,
        statusCode: result?.payload?.statusCode,
        success: result?.payload?.success,
      };
      if (
        result?.payload?.statusCode === 409 ||
        result?.payload?.statusCode === 400 ||
        result?.payload?.statusCode === 404
      ) {
        dispatch(updatenotificationdata(data));
      } else if (
        result?.payload?.statusCode === 201 ||
        result?.payload?.statusCode === 200
      ) {
        navigate("/QueriesKAM");
        dispatch(updatenotificationdata(data));
        dispatch(clearManualQuote());
        resetForm();
      }
    };

    if (Object.keys(manualQuoteDetails)?.length > 0) {
      payload.manualqueryId = manualQuoteDetails?._id;
      dispatch(editManualQuote(payload)).then(handleResult);
    } else {
      dispatch(addManualQuote(payload)).then(handleResult);
    }
  };

  return (
    <MainContainer>
      <CustomeHeader title="" description="" />
      <QueryDetailHeader queryDetails={queryDetails} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors, setSubmitting, resetForm }) => {
          onSubmit(values, setErrors, resetForm);
        }}
      >
        {(formik) => {
          const safeActiveIndex = Math.min(
            activeAirlineIndex,
            formik.values.airlines.length - 1
          );
          const activeAirline = formik.values.airlines[safeActiveIndex];
          const combinedGrandTotal = formik.values.airlines.reduce(
            (sum, airline) => sum + airlineGrandTotal(airline, totalWeight),
            0
          );

          // Every flight option across every airline, flattened — lets any
          // option's charge/itinerary sections offer "copy from" the others.
          const allOptionRefs = formik.values.airlines.flatMap((airline, ai) => {
            const airlineLabel =
              airLineList.find((a) => a.code === airline.carrierCode)?.name ||
              airline.carrierCode ||
              `New airline ${ai + 1}`;
            return airline.flightOptions.map((option, oi) => ({
              key: `${ai}-${oi}`,
              airlineIndex: ai,
              optionIndex: oi,
              label: `${airlineLabel} · Option ${oi + 1}`,
              option,
            }));
          });

          return (
            <div
              className="mt-5 rounded-2xl overflow-hidden"
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                boxShadow: "0 1px 3px rgba(16,24,40,0.04), 0 1px 2px rgba(16,24,40,0.03)",
              }}
            >
              <Form autoComplete="off">
                {/* Header: title + expiry inline */}
                <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
                  <h2 className="text-base font-bold text-gray-800">Quote details</h2>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 font-medium">Expiry</span>
                    <div className="datett relative">
                      <Field id="quoteExpiry" name="quoteExpiry">
                        {() => (
                          <DatePicker
                            value={
                              formik.values.quoteExpiry != null
                                ? dayjs(formik.values.quoteExpiry)
                                : null
                            }
                            showTime={{ use24Hours: true, format: "HH:mm", showSecond: false }}
                            format="YYYY-MM-DD HH:mm"
                            disabledDate={(current) =>
                              current &&
                              (current < dayjs().startOf("day") ||
                                current > dayjs().add(3, "month").endOf("day"))
                            }
                            className="text-sm rounded-lg h-9"
                            style={{ minWidth: "190px" }}
                            onChange={(date) =>
                              formik.setFieldValue("quoteExpiry", date)
                            }
                            placeholder="Select date & time"
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        className="dateerror"
                        name="quoteExpiry"
                        component={TextError}
                      />
                    </div>
                  </div>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {/* Airline tabs */}
                  <FieldArray name="airlines">
                    {({ push: pushAirline, remove: removeAirline }) => (
                      <AirlineTabs
                        airlines={formik.values.airlines}
                        activeIndex={safeActiveIndex}
                        onSelect={setActiveAirlineIndex}
                        onAdd={() => {
                          setActiveAirlineIndex(formik.values.airlines.length);
                          pushAirline(makeAirline());
                        }}
                        onRemove={(index) => {
                          removeAirline(index);
                          setActiveAirlineIndex((prev) => {
                            if (index === prev) return Math.max(0, index - 1);
                            if (index < prev) return prev - 1;
                            return prev;
                          });
                        }}
                        airlineNameByCode={(code) =>
                          airLineList.find((a) => a.code === code)?.name
                        }
                        totalWeight={totalWeight}
                      />
                    )}
                  </FieldArray>

                  {/* Active airline */}
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: "18px 20px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                      boxShadow: "0 1px 2px rgba(16,24,40,0.03)",
                    }}
                  >
                    <div style={{ maxWidth: 340 }}>
                      <label className={LABEL_CLS}>
                        Carrier <span className="text-red-500">*</span>
                      </label>
                      <Field
                        as={SelectMultipleWithSearch}
                        options={airLineList}
                        width="100%"
                        id={`airlines.${safeActiveIndex}.carrierCode`}
                        name={`airlines.${safeActiveIndex}.carrierCode`}
                        placeholder="Select carrier"
                        handleChange={formik.handleChange}
                        setFieldValue={(fieldName, value) => {
                          formik.setFieldValue(fieldName, value);
                          const match = airLineList.find((a) => a.code === value);
                          formik.setFieldValue(
                            `airlines.${safeActiveIndex}.carrierName`,
                            match?.name || ""
                          );
                        }}
                      />
                      <ErrorMessage
                        name={`airlines.${safeActiveIndex}.carrierCode`}
                        component={TextError}
                      />
                    </div>

                    <FieldArray name={`airlines.${safeActiveIndex}.flightOptions`}>
                      {({ push, insert, remove }) => (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                          {activeAirline.flightOptions.map((option, optionIndex) => (
                            <FlightOptionCard
                              key={optionIndex}
                              formikData={formik}
                              basePath={{
                                path: `airlines.${safeActiveIndex}.flightOptions.${optionIndex}`,
                                airlineIndex: safeActiveIndex,
                              }}
                              option={option}
                              optionIndex={optionIndex}
                              carrierCode={activeAirline.carrierCode}
                              chargeList={chargeList}
                              totalWeight={totalWeight}
                              airLineList={airLineList}
                              airPort={airPort}
                              originAirport={originAirport}
                              destAirport={destAirport}
                              allOptionRefs={allOptionRefs}
                              canRemove={activeAirline.flightOptions.length > 1}
                              onDuplicate={() => {
                                const clone = JSON.parse(JSON.stringify(option));
                                delete clone.optionId;
                                insert(optionIndex, clone);
                              }}
                              onRemove={() => remove(optionIndex)}
                            />
                          ))}

                          <button
                            type="button"
                            onClick={() => push(makeFlightOption(activeAirline.carrierCode))}
                            style={ADD_SECTION_BTN_STYLE}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#fffbf0")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <Plus size={15} />
                            Add flight option
                          </button>
                        </div>
                      )}
                    </FieldArray>
                  </div>

                  {formik?.errors?.formError && (
                    <p style={{ color: "#ef4444", fontSize: 13 }}>
                      {formik.errors.formError}
                    </p>
                  )}

                  {/* Totals + submit */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 flex-wrap gap-3">
                    <div style={HIGHLIGHT_CARD_STYLE}>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Combined grand total
                      </span>
                      <span className="text-base font-bold text-gray-800">
                        {money(combinedGrandTotal)}
                      </span>
                    </div>

                    <button
                      type="submit"
                      className="flex items-center gap-2 text-gray-900 font-semibold text-sm px-8 py-2.5 rounded-xl transition-colors"
                      style={{
                        background: "#FCB614",
                        boxShadow: "0 4px 10px rgba(252,182,20,0.28)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#e5a50e")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "#FCB614")
                      }
                    >
                      {loading ? (
                        <span>Processing...</span>
                      ) : (
                        <span>
                          {Object.keys(manualQuoteDetails)?.length > 0
                            ? "Update rates"
                            : "Submit rates"}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </Form>
            </div>
          );
        }}
      </Formik>
    </MainContainer>
  );
};

export default ManualQuote;
